import { type RefObject, useCallback, useEffect, useRef, useState } from 'react';

import { isRecordingListening, markListening, useAudioListening } from '../listening';
import {
  type DictationEngine,
  DictationError,
  type DictationStatus,
  type DictationStopReason,
  type DictationTurn,
  type PcmCaptureSession,
} from '../types';
import { mergeTurn } from '../utils/composeDictation';

import { useDictationRuntime } from './useDictationRuntime';

/**
 * Hard cap on one session. Someone who starts dictating and walks away must not
 * stream audio indefinitely; the backend's own cap is 5 minutes, this is tighter.
 */
export const DICTATION_SESSION_LIMIT_MS = 4 * 60_000;
const CLOSE_TIMEOUT_MS = 2_000;

export interface UseDictationOptions {
  /** Analytics label for the text field, e.g. `jot_support`. Never free text. */
  surface: string;
  keyterms?: string[];
  prompt?: string;
  /** Microphone to open; omitted means the browser default. */
  deviceId?: string;
  disabled?: boolean;
}

export interface DictationController {
  /** A runtime is mounted, enabled, and this control is not disabled. */
  available: boolean;
  status: DictationStatus;
  error: string | null;
  /** Turns of the current session, ordered. Cleared by `consumeTurns`. */
  turns: DictationTurn[];
  /** Latest microphone level, 0..1. Read from a rAF loop, never from render. */
  levelRef: RefObject<number>;
  start: () => void;
  stop: () => void;
  toggle: () => void;
  /** The host has folded `turns` into its value. */
  consumeTurns: () => void;
  dismissError: () => void;
}

interface Session {
  engine: DictationEngine;
  capture: PcmCaptureSession | null;
  connected: boolean;
  stopping: boolean;
  startedAt: number;
  finalTurns: Set<number>;
  limitTimer: number;
}

function snapshotRecording(): boolean {
  return isRecordingListening();
}

function describeError(error: unknown): string {
  if (error instanceof DictationError) return error.message;
  return 'Dictation could not start. Check your connection and try again.';
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T | undefined> {
  return Promise.race([
    promise,
    new Promise<undefined>(resolve => {
      window.setTimeout(() => resolve(undefined), ms);
    }),
  ]);
}

/**
 * State machine: idle → starting → listening → stopping → idle.
 * `starting` opens the microphone first (the visible, slow step), then mints a
 * token and connects the engine. Audio flows only while `listening`.
 */
export function useDictation(options: UseDictationOptions): DictationController {
  const runtime = useDictationRuntime();
  // An encounter recording owns the microphone; dictation steps aside.
  const recording = useAudioListening('recording');
  const [status, setStatus] = useState<DictationStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [turns, setTurns] = useState<DictationTurn[]>([]);
  const levelRef = useRef(0);
  const session = useRef<Session | null>(null);
  const mounted = useRef(true);
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const runtimeRef = useRef(runtime);
  runtimeRef.current = runtime;

  const available = Boolean(runtime?.enabled) && !options.disabled && !recording;

  const finish = useCallback(async (reason: DictationStopReason, message?: string) => {
    const current = session.current;
    if (!current || current.stopping) return;
    current.stopping = true;
    window.clearTimeout(current.limitTimer);
    current.capture?.stop();
    current.capture = null;
    levelRef.current = 0;
    if (mounted.current) setStatus('stopping');
    if (current.connected) {
      try {
        await withTimeout(current.engine.close(), CLOSE_TIMEOUT_MS);
      } catch {
        // The socket is already gone; nothing left to flush.
      }
    }
    if (session.current === current) session.current = null;
    // Only a session that reported `started` reports `exited`.
    if (current.connected) {
      runtimeRef.current?.track?.({
        type: 'exited',
      surface: optionsRef.current.surface,
      reason,
        durationMs: Date.now() - current.startedAt,
        turnCount: current.finalTurns.size,
      });
    }
    if (!mounted.current) return;
    if (message) setError(message);
    setStatus('idle');
  }, []);

  const start = useCallback(async () => {
    const rt = runtimeRef.current;
    if (!rt?.enabled || optionsRef.current.disabled || session.current) return;
    if (snapshotRecording()) return;
    const engine = rt.createEngine();
    const current: Session = {
      engine,
      capture: null,
      connected: false,
      stopping: false,
      startedAt: Date.now(),
      finalTurns: new Set(),
      limitTimer: 0,
    };
    session.current = current;
    setError(null);
    setTurns([]);
    setStatus('starting');
    try {
      const capture = await rt.startCapture(
        {
          onChunk: pcm => {
            if (session.current === current && current.connected && !current.stopping) {
              engine.sendAudio(pcm);
            }
          },
          onLevel: level => {
            levelRef.current = level;
          },
        },
        { deviceId: optionsRef.current.deviceId },
      );
      if (session.current !== current) {
        capture.stop();
        return;
      }
      current.capture = capture;
      const { token } = await rt.requestToken();
      if (session.current !== current) return;
      await engine.connect(
        {
          token,
          sampleRate: capture.sampleRate,
          keyterms: optionsRef.current.keyterms,
          prompt: optionsRef.current.prompt,
        },
        {
          onTurn: turn => {
            if (session.current !== current) return;
            if (turn.final) current.finalTurns.add(turn.order);
            setTurns(previous => mergeTurn(previous, turn));
          },
          onError: () => {
            if (session.current !== current) return;
            void finish('error', 'Dictation was interrupted. Start again to continue.');
          },
          onClose: ({ code }) => {
            if (session.current !== current || current.stopping) return;
            void finish(
              code === 1000 ? 'user' : 'error',
              code === 1000 ? undefined : 'Dictation disconnected. Start again to continue.',
            );
          },
        },
      );
      if (session.current !== current) {
        void engine.close();
        return;
      }
      current.connected = true;
      current.limitTimer = window.setTimeout(() => {
        void finish('session_limit', 'Dictation stops after 4 minutes. Start again to continue.');
      }, DICTATION_SESSION_LIMIT_MS);
      setStatus('listening');
      rt.track?.({ type: 'started', surface: optionsRef.current.surface });
    } catch (caught) {
      current.capture?.stop();
      current.capture = null;
      // An abandoned start (user cancelled, or started again) fails quietly;
      // its error must not land on whatever session replaced it.
      if (session.current !== current) return;
      session.current = null;
      if (!mounted.current) return;
      setError(describeError(caught));
      setStatus('idle');
    }
  }, [finish]);

  const stop = useCallback(() => {
    const current = session.current;
    if (!current) return;
    if (!current.connected) {
      // Still starting: abandon the attempt. `start` notices the cleared session.
      current.capture?.stop();
      session.current = null;
      levelRef.current = 0;
      setStatus('idle');
      return;
    }
    void finish('user');
  }, [finish]);

  const toggle = useCallback(() => {
    if (session.current) stop();
    else void start();
  }, [start, stop]);

  const consumeTurns = useCallback(() => setTurns([]), []);
  const dismissError = useCallback(() => setError(null), []);

  useEffect(() => {
    if (status !== 'listening') return undefined;
    return markListening('dictation');
  }, [status]);

  // An encounter recording that starts mid-dictation takes the microphone.
  useEffect(() => {
    if (recording && session.current) {
      void finish('recording_started', 'Dictation stopped because an encounter recording started.');
    }
  }, [recording, finish]);

  // A host that disables the field mid-session hides the controls; end the
  // session too so the microphone and socket never outlive their button.
  useEffect(() => {
    if (options.disabled && session.current) stop();
  }, [options.disabled, stop]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      void finish('unmount');
    };
  }, [finish]);

  return {
    available,
    status,
    error,
    turns,
    levelRef,
    start: () => void start(),
    stop,
    toggle,
    consumeTurns,
    dismissError,
  };
}
